import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Headphones,
  Download,
  ArrowRight,
  Radio,
  Sliders,
  Brain,
  Moon,
  Target,
  Heart,
  Save,
  Smartphone,
} from "lucide-react";
import NeonButton from "@/components/NeonButton";
import AudioWaveDecor from "@/components/AudioWaveDecor";
import CrystalIcon from "@/components/CrystalIcon";
import { useCosmicParallax } from "@/hooks/useCosmicParallax";

const features = [
  {
    icon: Radio,
    title: "Frequency Generator",
    description:
      "Design sounds from 0.1 Hz to 20,000 Hz. Layer up to 4 frequencies with sine, square, sawtooth waves.",
  },
  {
    icon: Headphones,
    title: "Binaural Beats",
    description:
      "Delta for sleep, Theta for meditation, Alpha for relaxation, Beta for focus, Gamma for peak performance.",
  },
  {
    icon: Sliders,
    title: "Background Sounds",
    description:
      "White, Pink, Brown, and Blue noise. Perfect for masking, sleep, focus, and tinnitus relief.",
  },
  {
    icon: Sparkles,
    title: "AI Subliminals",
    description:
      "Transform affirmations into whisper-soft audio using AI text-to-speech technology.",
  },
  {
    icon: Brain,
    title: "Sacred Frequencies",
    description:
      "432Hz, 528Hz, 639Hz and more. Healing frequencies for manifestation and transformation.",
  },
  {
    icon: Download,
    title: "Export & Download",
    description:
      "Download your custom audio as MP3. Take your sound therapy anywhere.",
  },
];

/** Crystalline framed icon — now imported from shared component */

const Landing = () => {
  const { mx, my } = useCosmicParallax();
  // Subtle counter-tilt for hero image (moves opposite to bg layers for depth illusion)
  const heroTx = -mx * 14;
  const heroTy = -my * 14;

  return (
    <div className="min-h-screen pb-32 relative" data-testid="landing-page">
      {/* ──────────────────────────────────────────────────────────
          HERO — Deep Void First. Signal Second.
          The cosmic energy extends BEYOND the viewport — infinite.
          ────────────────────────────────────────────────────────── */}
      <section
        className="relative w-full overflow-hidden flex flex-col"
        style={{ height: "100vh" }}
        data-testid="hero-section"
      >
        {/* Top bleed — pure black fade into nav */}
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black to-transparent pointer-events-none z-[3]" />

        {/* HERO ARTWORK REGION — takes most of the section, allows horizontal overflow */}
        <div className="relative flex-1 overflow-hidden">
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
            style={{
              transform: `translate3d(${heroTx}px, ${heroTy * 0.6}px, 0)`,
              willChange: "transform",
            }}
          >
            <img
              src="/assets/subspace-hero-square.png"
              alt="Subspace — Expand Your Mind"
              className="void-hero-img select-none"
              draggable="false"
              data-testid="hero-transmission"
              style={{
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </motion.div>
        </div>

        {/* Deep void fade above the CTA region */}
        <div className="absolute inset-x-0 bottom-32 md:bottom-36 h-40 bg-gradient-to-b from-transparent to-black/95 pointer-events-none z-[2]" />

        {/* CTA strip — fixed bottom band, clear of artwork */}
        <div className="relative z-10 pb-24 md:pb-28 pt-2 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link to="/studio" data-testid="hero-studio-btn">
                <NeonButton size="lg" className="group">
                  Open Sound Studio
                  <ArrowRight
                    size={18}
                    className="inline ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </NeonButton>
              </Link>
              <Link to="/create" data-testid="hero-subliminals-btn">
                <NeonButton variant="secondary" size="lg">
                  Create Subliminals
                </NeonButton>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 sm:gap-10 text-gray-400">
              {[432, 528, 639, 852].map((freq, i) => (
                <div
                  key={freq}
                  className="font-mono text-xs sm:text-sm frequency-pulse"
                  style={{ animationDelay: `${i * 0.5}s` }}
                >
                  <span className="text-fuchsia-400">{freq}</span>{" "}
                  <span className="text-gray-500">Hz</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You Can Do */}
      <section className="relative px-4 py-32 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 relative z-10"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3">
            Expand Your Mind.
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What You Can Do With <span className="text-neon">Subspace</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            It's not a playlist. It's an interface for your nervous system.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16 relative z-10">
          {[
            { icon: Moon, title: "Deep, Restful Sleep", desc: "Guide your body into rest the way it's always known how." },
            { icon: Target, title: "Instant Mental Clarity", desc: "Cut through mental noise and sharpen focus fast." },
            { icon: Heart, title: "Emotional Balance", desc: "Release tension and bring your nervous system back into balance." },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <CrystalIcon icon={item.icon} accent={`v${i}`} />
              <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="relative px-4 py-32 max-w-7xl mx-auto">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="tagline-shimmer text-xs font-semibold uppercase mb-3">
              Expand Your Mind.
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              You Control the <span className="text-neon">Sound</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Every sound is customizable. Every session is yours.
            </p>
            <div className="max-w-sm mx-auto mt-6">
              <AudioWaveDecor variant="bars" bars={28} height={32} />
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="crystal-card group">
                  <CrystalIcon icon={feature.icon} accent={`f${index}`} />
                  <h3 className="text-lg font-semibold text-white mb-2 text-center">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed text-center">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Daily Use */}
      <section className="relative px-4 py-32 max-w-5xl mx-auto">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for <span className="text-neon">Daily Use</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Download, title: "Download", desc: "Export audio files up to 59 minutes" },
              { icon: Save, title: "Save", desc: "Save combinations as presets" },
              { icon: Smartphone, title: "Any Device", desc: "Works on phone, tablet, desktop" },
              { icon: Headphones, title: "Anywhere", desc: "Use anytime, anywhere" },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="crystal-card text-center">
                  <CrystalIcon icon={item.icon} accent={`d${i}`} />
                  <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-4 py-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <div className="crystal-card crystal-card-glow text-center py-16 px-8 relative overflow-hidden">
            <div className="relative z-10">
              <p className="tagline-shimmer text-xs font-semibold uppercase mb-4">
                Expand Your Mind.
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Your Nervous System.{" "}
                <span className="text-neon">Your Control.</span>
              </h2>
              <p className="text-gray-300 mb-8 max-w-lg mx-auto">
                Reset your mind in seconds. All you need is your device.
              </p>
              <Link to="/studio" data-testid="cta-studio-btn">
                <NeonButton size="lg">Enter Subspace</NeonButton>
              </Link>
              <div className="max-w-md mx-auto mt-10">
                <AudioWaveDecor variant="line" height={40} />
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Landing;
